import React, { Component } from "react";
import { OmniTooltipNextComponent, OmniIconComponent, OmniToolbarComponent, OmniIconMenu, OmniDropdownComponent, AlertMessagesService, Utils, trackEvent, InlineEditDialogBox, AGGRID_LICENCE_KEY, creativeMap } from "../../../../shared";
import { CreateCreativeService } from "../../";
import { PlacementDetailsTableService, PlacementUtils } from "../../../placements";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);
LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);

export class CreativeAgGridComponent extends Component {

    constructor(props) {
        super(props);
        this.creativeGridRef = React.createRef();
        this.state = {
          selectedCreatives: [],
          isShowModal: false,
          editableData: {},
        };
    }
    openPopupElm = '';
    isInlineOverride = false;

    /**
     * Lifecycle method to handle file upload listener on component mount.
     * @function componentDidMount
     */
    componentDidMount = () => {
        this.props.fileUploadListner(this.props.tableData.id);
    }

    /**
     * Lifecycle method to update editable data when modal is open and data changes.
     * @function componentDidUpdate
     * @param {object} prevProp - Previous props.
     * @param {object} prevState - Previous state.
     */
    componentDidUpdate = (prevProp, prevState) => {
        if (this.state.isShowModal && this.state.editableData && Object.keys(this.state.editableData).length > 0) {
            let updatedEditData = this.props.tableData.taxonomyTableData.filter((data) => data.creative_id === this.state.editableData.creative_id);
            if (updatedEditData[0]?.updated_at !== this.state.editableData.updated_at) {
                this.setState({ editableData: updatedEditData[0] });
            }
        }
    }

    /**
     * Renders action buttons (Copy and Delete) for each row in the AG Grid.
     * @function actionCellRenderer
     * @param {object} props - Props for the cell renderer.
     * @returns {JSX.Element} - Rendered action buttons.
     */
    actionCellRenderer = (props) => {
        return (
            <div className="table-row-actions">
                <OmniTooltipNextComponent>
                    <button slot="invoker" aria-haspopup="true" className="button is-text is-shadowless"
                        onClick={() => { this.copyCreative(props) }}>
                        <OmniIconComponent icon-id="omni:interactive:copy" style={{width: "18px"}}></OmniIconComponent>
                    </button>
                    <div slot="content">Copy</div>
                </OmniTooltipNextComponent>
                <OmniTooltipNextComponent>
                    <button slot="invoker" aria-haspopup="true" className="button is-text is-shadowless"
                        onClick={() => { this.deleteCreative(props) }}>
                        <OmniIconComponent icon-id="omni:interactive:delete" style={{width: "18px"}}></OmniIconComponent>
                    </button>
                    <div slot="content">Delete</div>
                </OmniTooltipNextComponent>
            </div>
        )
    }

    /**
     * Default column definitions for the AG Grid.
     * @type {object}
     */
    defaultColDef = {
        flex: 1,
        filter: true,
        wrapText: true,
        floatingFilter: true,
        autoHeight: true,
        suppressHeaderMenuButton: true,
        cellStyle: params => {
            if (params?.data?.highlight) {
                return { backgroundColor: "#F9ECD2" };
            }
        }
    };
    

    /**
     * column definitions for the AG Grid.
     * @type {object}
     */
    creativeColumnDefs = [
        {
            headerName: "SUPPLIER CODE",
            field: "supplierCode",
            width: 180,
            flex: null,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "SITE",
            field: "site",
            width: 180,
            flex: null,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "CREATIVE STRING",
            field: "taxonomyValue",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "ACTIONS",
            sortable: false,
            filter: false,
            width: 120,
            pinned: 'right',
            cellRenderer: this.actionCellRenderer
        }
    ]

    /**
     * Handles selection of creatives in the AG Grid.
     * @function onCreativeSelection
     */
    onCreativeSelection = () => {
        let selectedNode = [];
        this.creativeGridRef.current.api.forEachNode((node) => {
            if (node.isSelected()) {
                selectedNode.push(node.data);
            }
        });
        this.setState({ selectedCreatives: selectedNode });
    }

    /**
     * Gets taxonomy configuration for a given taxonomy ID.
     * @function getTaxonomyConfig
     * @param {number} taxonomyId - The taxonomy ID to get configuration for.
     * @returns {object|null} - The taxonomy configuration or null if not found.
     */
    getTaxonomyConfig = (taxonomyId) => {
        if (this.props.allTaxonomyData && Object.values(this.props.allTaxonomyData).length > 0) {
            return Object.values(this.props.allTaxonomyData).find(
                taxonomy => taxonomy && Number(taxonomy.id) === Number(taxonomyId)
            );
        }
        return null;
    }

   /**
     * Copies a creative by calling the create creative API.
     * @function copyCreative
     * @param {object} props - Props containing the creative data.
     */
    copyCreative = (props) => {
        this.props.setSpinnerCount(1);
        let creative = props?.data;
        trackEvent('Creative copy requested', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, creativeId: creative?.creative_id });

        // Get taxonomy configuration for text case transformation
        let taxonomyConfig = this.getTaxonomyConfig(creative.taxonomyId);
        let creativeName = creative.taxonomyValue.split(":|")[0];
        if (creative.taxonomy) {
            const { taxonomyObjArray, mediaTaxonomy } = PlacementUtils.insertTaxonomyValueIdsIntoTaxonomy(taxonomyConfig?.fields, creative.taxonomy, true, creativeName, creativeName);
            creative.taxonomy = taxonomyObjArray;
            creativeName = mediaTaxonomy;
        }
        // Apply text case transformation using utility function
        creativeName = PlacementUtils.transformValueToGivenCase(creativeName, taxonomyConfig?.taxonomyTextcase);
        let payload = [{
            "creative_name": creativeName,
            "creative_type": creative.taxonomyName,
            "creative_display_name": creativeName,
            "taxonomy_schema": creative.taxonomySchema,
            "taxonomy_schema_name": creative.taxonomy_schema_name,
            "supplier": creative.supplier,
            "supplier_code": creative.supplier_code,
            "taxonomy_schema_id": creative.taxonomyId,
            "taxonomy_client_id": taxonomyConfig?.clientid,
            "is_override": creative.isOverride,
            "campaign_id": this.props.campaignId,
            "taxonomy": creative.taxonomy ? creative.taxonomy : null,
            "dimensions": creative.dimensions,
            "dimensions_position": creative.dimensions_position
        }];
        CreateCreativeService.createCreative(payload)
            .then((response) => { this.props.allCreativeFunc("Creative has been copied successfully"); trackEvent('Creative copied', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId }); })
            .catch((err) => { AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.copyCreative(props), title: 'Unable to copy the creative', bodyMsgArr: [] }]); trackEvent('Creative copy failed', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, error: err?.response?.data || err }); })
            .finally(() => this.props.setSpinnerCount(0));
    }

    /**
     * Deletes a creative by calling the delete creative API.
     * @function deleteCreative
     * @param {object} props - Props containing the creative data.
     */
    deleteCreative = (props) => {
        this.props.setSpinnerCount(1);
        let creative = props?.data;
        trackEvent('Creative delete requested', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, creativeId: creative?.creative_id });
        let payload = [{
            "creative_id": creative.creative_id,
            "is_delete": true
        }]
        CreateCreativeService.deleteCreative(payload)
            .then(() => { 
                this.props.allCreativeFunc("Creative has been deleted successfully"); 
                trackEvent('Creative deleted', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId }); 
            })
            .catch((err) => { 
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.deleteCreative(props), title: 'Unable to delete the creative', bodyMsgArr: [] }]); 
                trackEvent('Creative delete failed', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, error: err?.response?.data || err }); 
            })
            .finally(() => this.props.setSpinnerCount(0));
    };

    /**
     * Handles import/export actions for creatives.
     * @function importExportClick
     * @param {object} event - Event object containing the action details.
     */
    importExportClick = (event) => {
        const label = event?.detail?.value?.label;
        if (label.includes("Export")) {
            if (this.state.selectedCreatives.length) {
                this.setState({ spinnerCount: 1 });
                let payload = {
                  "creative_ids": this.state.selectedCreatives.map(creative => creative._id),
                  "taxonomytype-id": this.state.selectedCreatives[0]?.taxonomyId
                }
                trackEvent('Creatives export initiated', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, count: this.state.selectedCreatives.length, taxonomyId: payload["taxonomytype-id"] });
                this.exportCreatives(payload);
            } else {
                AlertMessagesService.showWarningToastMessage(`No Creative(s) selected, Please select at least 1 to export.`);
                trackEvent('Creatives export attempted with no selection', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId });
            }
        } else {
            document.getElementById(`${this.props.tableData.id}-taxonomyInput`).click();
            trackEvent('Creatives import file chooser opened', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId });
        }
    }

    /**
     * Exports selected creatives by calling the export creative API.
     * @function exportCreatives
     */
    exportCreatives = (payload) => {
        this.props.setSpinnerCount(1);
        CreateCreativeService.exportCreative(payload).then((response) => {
            AlertMessagesService.showInfoToastMessage('Preparing your file. We’ll let you know when it’s ready');
            trackEvent('Creatives export job triggered', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId });
            this.checkJobStatusForExportCreative(payload, response.job_id);
            this.props.setSpinnerCount(0);
        }, (error) => {
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.exportCreatives(payload), title: `Unable to export creatives`, bodyMsgArr: [] }]);
            trackEvent('Creatives export failed to start', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, error: error?.response?.data || error });
            this.props.setSpinnerCount(0);
        })
    }

     /**
     * Function will call API repeatly to check job status
     * then it will call function on job "completion" that will open download modal
     * @param {Array} selectedItems 
     * @param {Array} pmdsItems 
     * @param {string} jobId 
     */
    checkJobStatusForExportCreative = (payload, jobId) => {
        let jobIdPayload = { jobId: jobId };
        PlacementDetailsTableService.checkExportJobStatus(jobIdPayload)
      .then((response) => {
        if (response.status === "COMPLETED" && response.s3PresignedUrl) {
          Utils.showDownloadModal(response);
          trackEvent('Creatives exported', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, count: this.state.selectedCreatives?.length });
        } else if (response.status === "IN PROGRESS" || response.status === "TRIGGERED") {
          setTimeout(() => {
            this.checkJobStatusForExportCreative(payload, jobId);
          }, 10000);
        } else {
          AlertMessagesService.showErrorToastMessage("Set up your server to enable file download. Contact your team administrator for assistance.");
          trackEvent('Creatives export failed', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, status: response?.status });
        }
      }, (error) => {
        AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.exportCreatives(payload), title: `Unable to export creatives`, bodyMsgArr: [] }]);
        trackEvent('Creatives export failed', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, error: error?.response?.data || error });
      });
    }

    /**
     * Handles cell click events in the AG Grid.
     * @function onCellClicked
     * @param {object} props - Props containing the clicked cell data.
     */
    onCellClicked = (props) => {
        if(props.colDef.field === 'taxonomyValue'){
            let editableData = props.data;
            trackEvent('Creative inline edit opened', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, creativeId: editableData?.creative_id });
            let creativeName = editableData.creative_name.split(':|');
            let taxonomyName = creativeName[0];
            let taxonomySchemaArray = this.getTaxonomyConfig(editableData.taxonomyId);
            // If taxonomy schema is not found, fetch the latest taxonomy schema from allTaxonomyData
            if(!taxonomySchemaArray){
                let swappedCreativeMap = Utils.swapJson(creativeMap);
                taxonomySchemaArray = this.props.allTaxonomyData[swappedCreativeMap[editableData?.creative_type]];
            }
            let taxonomyData = taxonomySchemaArray?.fields;
            let taxonomyJson = editableData.taxonomy ? editableData.taxonomy : null;
            let taxonomySchema = editableData.taxonomySchema;
            //if creative is too old and there is taxonomy_ids_schema
            if(!taxonomyJson?.length && editableData?.taxonomy_ids_schema){
                let taxonomyIds = editableData?.taxonomy_ids_schema.split('|').map(val=> {
                    return val.split(' *')[1]
                });
            
                let newSchema = [];
                taxonomyIds.forEach((id,index) => { 
                    let field = {};
                    field = taxonomySchemaArray?.fields.find(field=> id == field.id);
                    let name = field?.name || field?.alias;
                    newSchema.push(name);
                });

                taxonomySchema = newSchema.join('|');
                editableData.taxonomy_schema = taxonomySchema;
                editableData.taxonomySchema = taxonomySchema;
            }
            let editFormData = PlacementDetailsTableService.splitPlacementStringIntoSchemaObject(taxonomyName, taxonomySchema, taxonomySchemaArray?.fields, ':|', false, taxonomyJson);
            let isNameAndSchemaFieldMatch = PlacementDetailsTableService.compareTaxonomyAndData(taxonomyData, editFormData, editableData?.taxonomySchema, editableData?.taxonomy);
            if(editableData.is_override || !isNameAndSchemaFieldMatch){
                this.isInlineOverride = true;
                // AlertMessagesService.showWarningToastMessage('Inline edit is disabled because of taxonomy mismatch or an overridden name')
                // return
            }
            editableData.id = editableData?.id ? editableData.id : editableData.creative_id;
            const taxonomyKeys = {
                'schema' : 'taxonomy_schema', 
                'displayName' : 'creative_display_name', 
                'taxonomyName' : 'creative_name', 
                'syntheticId' : editableData.creative_id
            }
            this.setState({ isShowModal: true, editableData,editFormData,taxonomySchemaArray,taxonomyKeys});
        }
    }

    /**
     * Closes the modal for editing creative names.
     * @function closeModal
     */
    closeModal = (isSuccess) => {
        this.isInlineOverride = false;
        this.setState({ isShowModal: false, editableData: {} });
        if(isSuccess) {
            this.props.allCreativeFunc("Creative has been updated successfully");
            trackEvent('Creative inline edit updated', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, creativeId: this.state?.editableData?.creative_id });
        } else {
            trackEvent('Creative inline edit closed', { access_from: 'Create Creative Page', campaignId: this.props?.campaignId, creativeId: this.state?.editableData?.creative_id });
        }
    }

    render() {
        return (
            <>
                <OmniToolbarComponent slot="header" className="margin5" key={`toolbar-${this.props.tableData.taxonomy_id}`}>
                    <div slot="end" className="custom-dropdown-menu">
                        <OmniIconMenu
                            options={[`Import Creative(s)`, `Export Creative(s)`]}
                            key={`icon-${this.props.tableData.taxonomy_id}`}
                            iconId="omni:interactive:importExport"
                            iconClass="is-size-4 header-icon"
                            tooltip="Import/Export"
                            onDropdownChange={(event) => this.importExportClick(event)}
                        ></OmniIconMenu>
                    </div>
                </OmniToolbarComponent>
                <div style={{ width: "100%", height: '100vh' }}>
                    <AgGridReact
                        key={this.props.tableData.taxonomy_id}
                        ref={this.creativeGridRef}
                        columnDefs={this.creativeColumnDefs}
                        rowData={this.props.tableData.taxonomyTableData}
                        getRowId={(params) => params?.data?._id?.toString()}
                        rowSelection={{ mode: "multiRow" }}
                        onSelectionChanged={this.onCreativeSelection}
                        defaultColDef={this.defaultColDef}
                        onCellClicked={this.onCellClicked}
                        domLayout="normal"
                    />
                </div>
                {/* Inline Edit Dialog */}
                {this.state.isShowModal && this.state?.editableData && <InlineEditDialogBox
                    selectedRowData={this.state.editableData}
                    onInlineModalClose={this.closeModal}
                    displayInlineEditModal ={this.state.isShowModal} 
                    editFormData = {this.state?.editFormData}
                    isOverride = { this.isInlineOverride }
                    taxonomySchemaArray = {this.state?.taxonomySchemaArray}
                    taxonomyMap ={null} 
                    allTaxonomyData = {this.props.allTaxonomyData}
                    systemParametersData = {this.props.systemParametersData}
                    campaignObj = {this.props.campaignObj}
                    omniProfile = {this.props.omniProfile}
                    versionOneApi = {this.props?.versionOneApi}
                    setSpinnerCount={this.props.setSpinnerCount}                         
                    taxonomyKeys={this.state.taxonomyKeys}                         
                /> } 
            </>
        );
    }
}